import dayjs from 'dayjs'
import React from 'react'

import type { PaginationProps, TableHeaders, TableProps } from '@fourthwall/components'
import { Tag, useMediaQueryContext, usePagination } from '@fourthwall/components'
import type { GetMembersParams } from '@fourthwall/services/dist/MembershipsCreatorApiClient'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'

import { reverse, routing } from '../../../../../../utils/routing'
import type { Member } from '../../../types'
import { MembershipCell } from '../components/MembershipCell'
import { MobileCell } from '../components/MobileCell'
import { SupporterCell } from '../components/SupporterCell'
import type { FilterType } from '../modules/MembersFilter/types'
import { useFilterMembers } from './useFilterMembers'
import { useMembersQuery } from './useMembersQuery'

const MEMBERS_LIMIT = 10

export const useMembers = () => {
  const [currentPage, setCurrentPage] = usePagination()
  const { filtersParams, searchQueryParam } = useFilterMembers()
  const media = useMediaQueryContext()
  const { timezoneAbbreviation } = useShopTime()

  const getStates = () => {
    const types = filtersParams.type?.split(',') as unknown as FilterType[]
    const states = filtersParams.state?.split(',')

    if (types?.length === 1 && types[0] === 'free') {
      return ['None', 'Former'] as GetMembersParams['states[]']
    }

    if (states) {
      return states as GetMembersParams['states[]']
    }

    if (types?.length === 1 && types[0] === 'member') {
      return ['Active', 'Expiring', 'PastDue', 'FreeTrial'] as GetMembersParams['states[]']
    }

    return states as GetMembersParams['states[]']
  }

  const intervals = filtersParams.billingCycle?.split(',') as GetMembersParams['intervals[]']
  const tierIds = filtersParams.tier?.split(',') as GetMembersParams['tier_ids[]']

  const { membersQuery } = useMembersQuery([
    {
      page: currentPage,
      limit: MEMBERS_LIMIT,
      'states[]': getStates(),
      'intervals[]': intervals,
      'tier_ids[]': tierIds,
      search_query: searchQueryParam || undefined,
      gifted_by_others: filtersParams.giftedByOthers ? true : undefined,
    },
  ])

  const getMembershipCell = (member: Member) => {
    if (member.state.type === 'Active') {
      const monthsCount = dayjs(new Date()).diff(dayjs(member.state.subscribedAt), 'months')
      const tagLabel = monthsCount ? `${monthsCount}-month` : 'New'

      return (
        <MembershipCell
          info={`${member.tier?.name} tier${member.state.freeTrialEndsAt ? ' (Free trial)' : ''}`}
          tags={<Tag label={`${tagLabel} member`} appearance="success" />}
        />
      )
    }

    if (member.state.type === 'Expiring') {
      return (
        <MembershipCell
          info={
            <>
              Expires <TimezonedDateString date={member.state.expiresAt} format="MMM D, YYYY" />
            </>
          }
          tags={<Tag label="Expiring member" appearance="alert" />}
        />
      )
    }

    if (member.state.type === 'PastDue') {
      return (
        <MembershipCell
          info={
            <>
              Payment failed{' '}
              <TimezonedDateString date={member.state.previousPeriodEnd} format="MMM D, YYYY" />
            </>
          }
          tags={<Tag label="Past due member" appearance="alert" />}
        />
      )
    }

    if (member.state.type === 'Former') {
      return (
        <MembershipCell
          info={
            <>
              Expired <TimezonedDateString date={member.state.expiredAt} format="MMM D, YYYY" />
            </>
          }
          tags={
            <>
              <Tag label="Former member" appearance="default" />
              <Tag label="Free account" appearance="default" />
            </>
          }
        />
      )
    }

    if (member.state.type === 'None') {
      return <MembershipCell tags={<Tag label="Free account" appearance="default" />} />
    }
  }

  const mobileHeaders: TableHeaders<Member> = [
    {
      id: 'supporter',
      label: undefined,
      render: (member) => {
        return (
          <MobileCell
            name={member.nickname}
            avatarUrl={member.avatarUrl}
            shadowbanned={member.shadowBanned}
          >
            {getMembershipCell(member)}
          </MobileCell>
        )
      },
    },
  ]

  const headers: TableHeaders<Member> = [
    {
      id: 'supporter',
      label: 'Supporter',
      render: (member) => {
        return (
          <SupporterCell
            name={member.nickname}
            avatarUrl={member.avatarUrl}
            shadowBanned={member.shadowBanned}
          />
        )
      },
      width: 2,
    },
    {
      id: 'membership',
      label: 'Membership',
      render: getMembershipCell,
    },
    {
      id: 'joined_date',
      label: `Joined date (${timezoneAbbreviation})`,
      render: (member) => <TimezonedDateString date={member.joinedAt} format="MMM D, YYYY" />,
    },
  ]

  const table: TableProps = {
    data: membersQuery.data?.members || [],
    headers: media.laptop ? headers : mobileHeaders,
    // @ts-ignore
    getRowLink: ({ id }: { id: string }) => ({
      pathname: reverse(routing.memberships.members.member.self, { memberId: id }),
      search: location.search,
    }),
  }

  const pagination: PaginationProps = {
    pageCount: membersQuery.data?.pagination?.pages || 1,
    currentPage,
    total: membersQuery.data?.pagination?.total
      ? {
          count: membersQuery.data.pagination.total,
          limit: MEMBERS_LIMIT,
        }
      : undefined,
    onChange: setCurrentPage,
  }

  return {
    members: {
      table,
      pagination,
      isLoading: membersQuery.isLoading,
    },
  }
}
